import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { CartProvider } from './contexts/CartContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { CustomerAuthProvider } from './contexts/CustomerAuthContext'
import { TenantProvider, useTenant } from './contexts/TenantContext'
import { LanguageScope } from './i18n'

// Public routes (Customer PWA)
import StorefrontIndex from './pages/customer/StorefrontIndex'
import MenuPage from './pages/customer/MenuPage'
import CartPage from './pages/customer/CartPage'
import CheckoutPage from './pages/customer/CheckoutPage'
import OrderStatusPage from './pages/customer/OrderStatusPage'
import ReservationPage from './pages/customer/ReservationPage'
import ProductDetailPage from './pages/customer/ProductDetailPage'
import ReservationsPage from './pages/admin/ReservationsPage'
import AccountPage from './pages/customer/AccountPage'

// Marketing (bare apex domain - no tenant resolved)
import LandingPage from './pages/marketing/LandingPage'
import GetStartedPage from './pages/marketing/GetStartedPage'
import { PricingPage, AboutPage, ContactPage, PrivacyPage, TermsPage, BlogPage, BlogPostPage } from './pages/marketing/SitePages'

// Protected routes (Admin Dashboard)
import LoginPage from './pages/admin/LoginPage'
import SsoPage from './pages/admin/SsoPage'
import DashboardPage from './pages/admin/DashboardPage'
import MenuManagementPage from './pages/admin/MenuManagementPage'
import TableManagementPage from './pages/admin/TableManagementPage'
import OrdersPage from './pages/admin/OrdersPage'
import KitchenDisplayPage from './pages/admin/KitchenDisplayPage'
import CashierDashboard from './pages/admin/CashierDashboard'
import POSPage from './pages/admin/POSPage'
import InventoryPage from './pages/admin/InventoryPage'
import ReportsPage from './pages/admin/ReportsPage'
import SettingsPage from './pages/admin/SettingsPage'
import WebsitePage from './pages/admin/WebsitePage'
import EmployeesPage from './pages/admin/EmployeesPage'
import CustomersPage from './pages/admin/CustomersPage'
import WhatsAppMarketingPage from './pages/admin/WhatsAppMarketingPage'
import ServicesManagementPage from './pages/admin/ServicesManagementPage'
import BookingsPage from './pages/admin/BookingsPage'
import BillingPage from './pages/admin/BillingPage'
import AvailabilityPage from './pages/admin/AvailabilityPage'
import ShippingPage from './pages/admin/ShippingPage'

// Owner routes (Multi-tenant Management)
import OwnerDashboard from './pages/owner/OwnerDashboard'
import RestaurantForm from './pages/owner/RestaurantForm'
import RestaurantDetails from './pages/owner/RestaurantDetails'
import ActivityLogs from './pages/owner/ActivityLogs'
import PlatformLayout from './components/layout/PlatformLayout'
import PlatformOverview from './pages/platform/PlatformOverview'
import PlatformBusinesses from './pages/platform/PlatformBusinesses'
import PlatformUsers from './pages/platform/PlatformUsers'
import PlatformPayments from './pages/platform/PlatformPayments'
import PlatformPlans from './pages/platform/PlatformPlans'
import PlatformActivity from './pages/platform/PlatformActivity'

// Layout components
import CustomerLayout from './components/layout/CustomerLayout'
import AdminLayout from './components/layout/AdminLayout'
import OwnerLayout from './components/layout/OwnerLayout'
import ProtectedRoute from './components/auth/ProtectedRoute'
import { t } from './i18n'

// The storefront speaks the language its owner chose in Settings (or lets
// customers switch when set to both); the bare marketing domain lets the
// visitor choose.
function StoreLanguage({ children }) {
  const tenant = useTenant()
  if (!tenant.found) return <LanguageScope key="marketing" scope="marketing">{children}</LanguageScope>
  const setting = tenant.settings?.store_language || 'en'
  return <LanguageScope key="store" scope="store" fixed={setting === 'both' ? null : setting} fallback="en">{children}</LanguageScope>
}

function App() {
  return (
    <ThemeProvider>
      <TenantProvider>
      <AuthProvider>
        <CustomerAuthProvider>
        <SocketProvider>
          <CartProvider>
            <Routes>
            {/* Get-started onboarding wizard - full-screen, no store chrome */}
            <Route path="/get-started" element={<LanguageScope scope="marketing"><GetStartedPage /></LanguageScope>} />

            {/* Marketing site (main domain only) */}
            <Route path="/pricing" element={<LanguageScope scope="marketing"><PricingPage /></LanguageScope>} />
            <Route path="/about" element={<LanguageScope scope="marketing"><AboutPage /></LanguageScope>} />
            <Route path="/contact" element={<LanguageScope scope="marketing"><ContactPage /></LanguageScope>} />
            <Route path="/privacy" element={<LanguageScope scope="marketing"><PrivacyPage /></LanguageScope>} />
            <Route path="/terms" element={<LanguageScope scope="marketing"><TermsPage /></LanguageScope>} />
            <Route path="/blog" element={<LanguageScope scope="marketing"><BlogPage /></LanguageScope>} />
            <Route path="/blog/:slug" element={<LanguageScope scope="marketing"><BlogPostPage /></LanguageScope>} />

            {/* Public Customer Routes */}
            <Route path="/" element={<StoreLanguage><CustomerLayout /></StoreLanguage>}>
              <Route index element={<StorefrontIndex />} />
              <Route path="menu" element={<StorefrontIndex />} />
              <Route path="product/:id" element={<ProductDetailPage />} />
              <Route path="reservations" element={<ReservationPage />} />
              <Route path="cart" element={<CartPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="order/:orderId" element={<OrderStatusPage />} />
              <Route path="order-status" element={<OrderStatusPage />} />
              <Route path="account" element={<AccountPage />} />
            </Route>

            {/* Admin Authentication */}
            <Route path="/admin/login" element={<LanguageScope scope="admin"><LoginPage /></LanguageScope>} />
            <Route path="/admin/sso" element={<LanguageScope scope="admin"><SsoPage /></LanguageScope>} />

            {/* Protected Admin Routes */}
            <Route path="/admin" element={
              <LanguageScope scope="admin">
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              </LanguageScope>
            }>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="menu" element={<MenuManagementPage />} />
              <Route path="products" element={<MenuManagementPage />} />
              <Route path="categories" element={<MenuManagementPage />} />
              <Route path="tables" element={<TableManagementPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="kitchen" element={<KitchenDisplayPage />} />
              <Route path="cashier" element={<CashierDashboard />} />
              <Route path="pos" element={<POSPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="employees" element={<EmployeesPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="services" element={<ServicesManagementPage />} />
              <Route path="bookings" element={<BookingsPage />} />
              <Route path="reservations" element={<ReservationsPage />} />
              <Route path="availability" element={<AvailabilityPage />} />
              <Route path="whatsapp" element={<WhatsAppMarketingPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="website" element={<WebsitePage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="shipping" element={<ShippingPage />} />
            </Route>

            {/* Protected Owner Routes (Multi-tenant Management) */}
            <Route path="/owner" element={
              <LanguageScope scope="admin">
                <ProtectedRoute requiredRoles={['owner']}>
                  <OwnerLayout />
                </ProtectedRoute>
              </LanguageScope>
            }>
              <Route index element={<OwnerDashboard />} />
              <Route path="dashboard" element={<OwnerDashboard />} />
              <Route path="restaurants/new" element={<RestaurantForm />} />
              <Route path="restaurants/:id" element={<RestaurantDetails />} />
              <Route path="restaurants/:id/edit" element={<RestaurantForm />} />
              <Route path="logs" element={<ActivityLogs />} />
              <Route path="plans" element={<Navigate to="/platform/plans" replace />} />
            </Route>

            {/* Platform console: the super admin manages every business */}
            <Route path="/platform" element={
              <LanguageScope scope="admin">
                <ProtectedRoute superAdmin>
                  <PlatformLayout />
                </ProtectedRoute>
              </LanguageScope>
            }>
              <Route index element={<PlatformOverview />} />
              <Route path="businesses" element={<PlatformBusinesses />} />
              <Route path="users" element={<PlatformUsers />} />
              <Route path="payments" element={<PlatformPayments />} />
              <Route path="plans" element={<PlatformPlans />} />
              <Route path="activity" element={<PlatformActivity />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
                  <p className="text-gray-600 mb-8">{t("Page not found")}</p>
                  <a 
                    href="/" 
                    className="btn-primary"
                  >
                    {t("Go Home")}
                  </a>
                </div>
              </div>
            } />
            </Routes>
          </CartProvider>
        </SocketProvider>
        </CustomerAuthProvider>
      </AuthProvider>
      </TenantProvider>
    </ThemeProvider>
  )
}

export default App
